import { List, Map } from 'immutable';

import { setCategoryErrors, setConditionErrors, setFilesErrors, setIsSupplyErrors, setItemSpecificsErrors, setMaterialsErrors, setProductDetailsErrors, setProductionPartnersErrors, setShippingErrors, setTaxonomyAttributesErrors, setVariationsErrors, setWhenMadeErrors, setWhoMadeErrors } from '../product/errors';
import { getAttributesForCategory, updateItemSpecificsForCategory, updateProductDetailsForCategory } from '../product/taxonomy';
import { shapeEbayDetailsForAPI, shapeTaxonomyAttributesForAPI } from '../product/shapeAttributes';
import { getMaterialsError } from '../validations/materials';
import { newShipping } from '../product/new';
import { getSize } from '../iterable/getSize';
import { keepOld } from '../iterable/keepOld';
import { get } from '../iterable/get';

import { AMOUNT, ERROR, TAXONOMY_PROPERTY, OPERATIONS, VALUE, NEW } from '../../constants/product';
import { EBAY, ETSY, FAIRE, SHOPIFY } from '../../constants/channels';
import { ATTRIBUTE } from '../../constants/attributes';
import { ERRORS } from '../../constants/validations';
import { MODALS } from '../../constants/modal';
import { EMPTY } from '../../constants';
import { FORM } from '../../constants/forms';

export function editDetails({ product: source, shopData, taxonomyData, ...values }) {
  let confirmation;
  let product = source;
  const operations = [];
  const channel = product.get('channel');

  switch (channel) {
    case EBAY: {
      if (values[ATTRIBUTE.ITEM_CATEGORY] !== undefined) {
        const type = OPERATIONS[EBAY].ITEM_CATEGORY;
        const { category } = values[ATTRIBUTE.ITEM_CATEGORY];

        product = product.setIn([FORM.DETAILS, ATTRIBUTE.ITEM_CATEGORY, VALUE], category);
        product = setCategoryErrors({ product, taxonomyData });
        product = setConditionErrors({ product, taxonomyData });
        product = setVariationsErrors({ product, shopData, taxonomyData });
        product = setItemSpecificsErrors({
          product: product.setIn([FORM.DETAILS, ATTRIBUTE.ITEM_SPECIFICS],
            updateItemSpecificsForCategory({
              category,
              channel,
              itemSpecifics: product.getIn([FORM.DETAILS, ATTRIBUTE.ITEM_SPECIFICS], EMPTY.MAP),
              taxonomyData,
            })
          ),
          shopData,
          taxonomyData,
        });

        product = setProductDetailsErrors({
          product: product.setIn([FORM.DETAILS, ATTRIBUTE.PRODUCT_DETAILS],
            updateProductDetailsForCategory({
              category,
              channel,
              productDetails: product.getIn([FORM.DETAILS, ATTRIBUTE.PRODUCT_DETAILS], EMPTY.MAP),
              taxonomyData,
            })
          ),
          taxonomyData,
        });

        if (
          !product.getIn([FORM.DETAILS, ATTRIBUTE.ITEM_CATEGORY, ERROR]) &&
          category !== source.getIn([FORM.DETAILS, ATTRIBUTE.ITEM_CATEGORY, VALUE])
        ) {
          operations.push(Map({ type, value: category === AMOUNT.ZERO ? null : category }));

          const conditions = get(['CATEGORIES', category, 'conditions'])(taxonomyData) || {};

          if (
            getSize(conditions.ids) === 1 &&
            product.getIn([FORM.DETAILS, ATTRIBUTE.CONDITION, VALUE]) !== conditions.ids[0]
          ) {
            values[ATTRIBUTE.CONDITION] = product
              .getIn([FORM.DETAILS, ATTRIBUTE.CONDITION])
              .set(VALUE, conditions.ids[0]);
          }
        }
      }

      if (values[ATTRIBUTE.CONDITION] !== undefined) {
        const value = values[ATTRIBUTE.CONDITION];

        product = setConditionErrors({
          product: product.setIn([FORM.DETAILS, ATTRIBUTE.CONDITION], value),
          taxonomyData,
        });

        const condition = value.get(VALUE);
        const description = value.get(ATTRIBUTE.DESCRIPTION);

        if (
          condition !== source.getIn([FORM.DETAILS, ATTRIBUTE.CONDITION, VALUE]) &&
          product.getIn([FORM.DETAILS, ATTRIBUTE.CONDITION, ERROR]) !== ERRORS.DETAILS.CONDITION_ID
        ) {
          operations.push(Map({ type: OPERATIONS[EBAY].CONDITION_ID, value: condition }));
        }

        if (
          condition &&
          condition !== '1000' &&
          description !== source.getIn([FORM.DETAILS, ATTRIBUTE.CONDITION, ATTRIBUTE.DESCRIPTION]) &&
          product.getIn([FORM.DETAILS, ATTRIBUTE.CONDITION, ERROR]) !== ERRORS.DETAILS.CONDITION_DESCRIPTION
        ) {
          operations.push(Map({ type: OPERATIONS[EBAY].CONDITION_DESCRIPTION, value: description }));
        }
      }

      if (values[ATTRIBUTE.ITEM_SPECIFICS] !== undefined) {
        const { key, specific } = values[ATTRIBUTE.ITEM_SPECIFICS];

        switch (specific.get('type')) {
          case TAXONOMY_PROPERTY.CUSTOM: {
            if (specific.get('value')) {
              if (!product.hasIn([FORM.DETAILS, ATTRIBUTE.ITEM_SPECIFICS, VALUE, key])) {
                const custom = product
                  .getIn([FORM.DETAILS, ATTRIBUTE.ITEM_SPECIFICS, 'custom'], EMPTY.LIST)
                  .push(key);

                product = product.setIn([FORM.DETAILS, ATTRIBUTE.ITEM_SPECIFICS, 'custom'], custom);
              }

              product = product.setIn([FORM.DETAILS, ATTRIBUTE.ITEM_SPECIFICS, VALUE, key], specific);
            } else {
              const custom = product
                .getIn([FORM.DETAILS, ATTRIBUTE.ITEM_SPECIFICS, 'custom'], EMPTY.LIST)
                .filter(function filterCustom(item) {
                  return item !== key;
                });

              product = product
                .deleteIn([FORM.DETAILS, ATTRIBUTE.ITEM_SPECIFICS, VALUE, key])
                .setIn([FORM.DETAILS, ATTRIBUTE.ITEM_SPECIFICS, 'custom'], custom);
            }

            break;
          }

          default: {
            product = getSize(specific.get('value'))
              ? product.setIn([FORM.DETAILS, ATTRIBUTE.ITEM_SPECIFICS, VALUE, key], specific)
              : product.deleteIn([FORM.DETAILS, ATTRIBUTE.ITEM_SPECIFICS, VALUE, key]);

            break;
          }
        }

        product = setItemSpecificsErrors({ product, shopData, taxonomyData });

        if (!product.hasIn([FORM.DETAILS, ATTRIBUTE.ITEM_SPECIFICS, ERROR])) {
          const itemSpecifics = product.getIn([FORM.DETAILS, ATTRIBUTE.ITEM_SPECIFICS], EMPTY.MAP);
          const value = shapeEbayDetailsForAPI({ details: itemSpecifics });

          operations.push(Map({ type: OPERATIONS[EBAY].ITEM_SPECIFICS.SET, value }));
        }
      }

      if (values[ATTRIBUTE.PRODUCT_DETAILS] !== undefined) {
        const { key, detail } = values[ATTRIBUTE.PRODUCT_DETAILS];

        product = getSize(detail.get('value'))
          ? product.setIn([FORM.DETAILS, ATTRIBUTE.PRODUCT_DETAILS, VALUE, key], detail)
          : product.deleteIn([FORM.DETAILS, ATTRIBUTE.PRODUCT_DETAILS, VALUE, key]);

        product = setProductDetailsErrors({ product, taxonomyData });

        if (!product.hasIn([FORM.DETAILS, ATTRIBUTE.PRODUCT_DETAILS, ERROR])) {
          const productDetails = product.getIn([FORM.DETAILS, ATTRIBUTE.PRODUCT_DETAILS], EMPTY.MAP);
          const value = shapeEbayDetailsForAPI({ details: productDetails });

          operations.push(Map({ type: OPERATIONS[EBAY].PRODUCT_DETAILS.SET, value }));
        }
      }

      if (values[ATTRIBUTE.STORE_CATEGORY] !== undefined) {
        const value = values[ATTRIBUTE.STORE_CATEGORY];
        product = product.setIn([FORM.DETAILS, ATTRIBUTE.STORE_CATEGORY, VALUE], value);
        operations.push(Map({ type: OPERATIONS[EBAY].STORE_CATEGORY, value }));
      }

      break;
    }

    case ETSY: {
      if (values[ATTRIBUTE.CATEGORY] !== undefined) {
        const type = OPERATIONS[ETSY].CATEGORY;
        const { category } = values[ATTRIBUTE.CATEGORY];
        operations.push(Map({ type, value: category }));
        product = setCategoryErrors({
          product: product.setIn([FORM.DETAILS, ATTRIBUTE.CATEGORY, VALUE], category || AMOUNT.ZERO),
        });

        const { changed, taxonomyAttributes } = getAttributesForCategory({
          category: product.getIn([FORM.DETAILS, ATTRIBUTE.CATEGORY, VALUE]),
          taxonomyAttributes: product.getIn([FORM.DETAILS, ATTRIBUTE.TAXONOMY_ATTRIBUTES], EMPTY.MAP),
        });

        product = setTaxonomyAttributesErrors({
          product: product.setIn([FORM.DETAILS, ATTRIBUTE.TAXONOMY_ATTRIBUTES], taxonomyAttributes),
        });

        if (changed) {
          confirmation = MODALS.CONFIRMATIONS.CHANGE_CATEGORY;
        }
      }

      if (values[ATTRIBUTE.SECTION] !== undefined) {
        const value = values[ATTRIBUTE.SECTION];
        operations.push(Map({ type: OPERATIONS[ETSY].SECTION, value: value || 'none' }));
        product = product.setIn([FORM.DETAILS, ATTRIBUTE.SECTION, VALUE], value || null);
      }

      if (values[ATTRIBUTE.SHOULD_AUTO_RENEW] !== undefined) {
        const value = values[ATTRIBUTE.SHOULD_AUTO_RENEW];
        operations.push(Map({ type: OPERATIONS[ETSY].SHOULD_AUTO_RENEW, value }));
        product = product.setIn([FORM.DETAILS, ATTRIBUTE.SHOULD_AUTO_RENEW, VALUE], value);
      }

      if (values[ATTRIBUTE.IS_SUPPLY] !== undefined) {
        const value = values[ATTRIBUTE.IS_SUPPLY];
        operations.push(Map({ type: OPERATIONS[ETSY].IS_SUPPLY, value }));
        product = product.setIn([FORM.DETAILS, ATTRIBUTE.IS_SUPPLY, VALUE], value);
        product = setIsSupplyErrors({ product });
        product = setProductionPartnersErrors({ product });
      }

      if (values[ATTRIBUTE.WHEN_MADE] !== undefined) {
        const value = values[ATTRIBUTE.WHEN_MADE];
        operations.push(Map({ type: OPERATIONS[ETSY].WHEN_MADE, value }));
        product = product.setIn([FORM.DETAILS, ATTRIBUTE.WHEN_MADE, VALUE], value);
        product = setWhenMadeErrors({ product });
        product = setProductionPartnersErrors({ product });
        product = setFilesErrors({ product });
      }

      if (values[ATTRIBUTE.WHO_MADE] !== undefined) {
        const value = values[ATTRIBUTE.WHO_MADE];
        operations.push(Map({ type: OPERATIONS[ETSY].WHO_MADE, value }));
        product = product.setIn([FORM.DETAILS, ATTRIBUTE.WHO_MADE, VALUE], value);
        product = setWhoMadeErrors({ product });
        product = setProductionPartnersErrors({ product });
      }

      if (values[ATTRIBUTE.PRODUCTION_PARTNERS] !== undefined) {
        const value = values[ATTRIBUTE.PRODUCTION_PARTNERS];
        operations.push(Map({ type: OPERATIONS[ETSY].PRODUCTION_PARTNERS, value }));
        product = product.setIn([FORM.DETAILS, ATTRIBUTE.PRODUCTION_PARTNERS, VALUE], value);
        product = setProductionPartnersErrors({ product });
      }

      if (values[ATTRIBUTE.DIGITAL] !== undefined) {
        const value = values[ATTRIBUTE.DIGITAL];
        operations.push(Map({ type: OPERATIONS[ETSY].DIGITAL, value }));
        product = product.setIn([FORM.DETAILS, ATTRIBUTE.DIGITAL, VALUE], value);

        if (value) {
          if (!product.hasIn([FORM.FILES, VALUE])) {
            product = product.setIn([FORM.FILES, VALUE], EMPTY.LIST);
          }
        } else {
          if (!product.has(FORM.SHIPPING)) {
            product = product.set(FORM.SHIPPING, newShipping({ channel }));
          }
        }

        product = setFilesErrors({ product });
        product = setShippingErrors({ product });
        product = setWhenMadeErrors({ product });
        product = setVariationsErrors({ product, shopData });
      }

      if (values[ATTRIBUTE.MATERIALS] !== undefined) {
        const { [ATTRIBUTE.MATERIALS]: { materials, newMaterials }} = values;

        product = product.setIn([FORM.DETAILS, ATTRIBUTE.MATERIALS, VALUE], materials);

        if (getSize(newMaterials)) {
          product = product.setIn([FORM.DETAILS, ATTRIBUTE.MATERIALS, NEW], newMaterials);
        } else if (getSize(product.setIn([FORM.DETAILS, ATTRIBUTE.MATERIALS, NEW]))) {
          product = product.deleteIn([FORM.DETAILS, ATTRIBUTE.MATERIALS, NEW], newMaterials);
        }

        const allMaterials = product
          .getIn([FORM.DETAILS, ATTRIBUTE.MATERIALS, VALUE])
          .mergeWith(
            keepOld,
            product.getIn([FORM.DETAILS, ATTRIBUTE.MATERIALS, NEW], EMPTY.ORDERED_MAP)
          );

        if (!getMaterialsError({ materials: allMaterials })) {
          product = setMaterialsErrors({ product });
          operations.push(Map({ type: OPERATIONS[ETSY].MATERIALS.CHANGE_TO, value: allMaterials.toList() }));
        }
      }

      if (values[ATTRIBUTE.TAXONOMY_ATTRIBUTES] !== undefined) {
        for (const key in values[ATTRIBUTE.TAXONOMY_ATTRIBUTES]) {
          if (values[ATTRIBUTE.TAXONOMY_ATTRIBUTES].hasOwnProperty(key)) {
            const propertyId = parseInt(key);
            const attribute = {
              scaleId: values[ATTRIBUTE.TAXONOMY_ATTRIBUTES][key].get('scaleId', null),
              values: values[ATTRIBUTE.TAXONOMY_ATTRIBUTES][key].get('values', EMPTY.LIST),
              valueIds: values[ATTRIBUTE.TAXONOMY_ATTRIBUTES][key].get('valueIds', EMPTY.LIST),
            };

            product = !attribute.scaleId && getSize(attribute.values) === 0 && getSize(attribute.valueIds) === 0
              ? product.setIn([FORM.DETAILS, ATTRIBUTE.TAXONOMY_ATTRIBUTES, propertyId], EMPTY.MAP)
              : product.setIn([FORM.DETAILS, ATTRIBUTE.TAXONOMY_ATTRIBUTES, propertyId], Map(attribute));

            product = setTaxonomyAttributesErrors({ product });
          }
        }

        const value = shapeTaxonomyAttributesForAPI({
          taxonomyAttributes: product.getIn([FORM.DETAILS, ATTRIBUTE.TAXONOMY_ATTRIBUTES]),
        });

        if (getSize(value) > 0) {
          operations.push(Map({ type: OPERATIONS[ETSY].TAXONOMY_ATTRIBUTES, value }));
        }
      }

      break;
    }

    case FAIRE: {
      if (values[ATTRIBUTE.CATEGORY] !== undefined) {
        const type = OPERATIONS[FAIRE].CATEGORY;
        const { category } = values[ATTRIBUTE.CATEGORY];
        operations.push(Map({ type, value: category }));
        product = product.setIn([FORM.DETAILS, ATTRIBUTE.CATEGORY, VALUE], category);
      }

      break;
    }

    case SHOPIFY: {
      if (values[ATTRIBUTE.CATEGORY] !== undefined) {
        const type = OPERATIONS[SHOPIFY].CATEGORY;
        const { category = null } = values[ATTRIBUTE.CATEGORY];
        operations.push(Map({ type, value: category }));
        product = product.setIn([FORM.DETAILS, ATTRIBUTE.CATEGORY, VALUE], category);
      }

      if (values[ATTRIBUTE.COLLECTIONS] !== undefined) {
        const collections = values[ATTRIBUTE.COLLECTIONS];
        product = product.setIn([FORM.DETAILS, ATTRIBUTE.COLLECTIONS, VALUE], collections);
        operations.push(
          Map({
            type: OPERATIONS[SHOPIFY].COLLECTIONS,
            value: getSize(collections) > 0 ? collections : List(['']),
          })
        );
      }

      if (values[ATTRIBUTE.PRODUCT_TYPE] !== undefined) {
        const value = values[ATTRIBUTE.PRODUCT_TYPE];
        product = product.setIn([FORM.DETAILS, ATTRIBUTE.PRODUCT_TYPE, VALUE], value || null);
        operations.push(Map({ type: OPERATIONS[SHOPIFY].PRODUCT_TYPE, value: value|| '' }));
      }

      if (values[ATTRIBUTE.VENDOR] !== undefined) {
        const value = values[ATTRIBUTE.VENDOR];
        product = product.setIn([FORM.DETAILS, ATTRIBUTE.VENDOR, VALUE], value || null);
        operations.push(Map({ type: OPERATIONS[SHOPIFY].VENDOR, value: value || '' }));
      }

      break;
    }

    default: {
      break;
    }
  }

  return { confirmation, product, operations };
}
